import { useMemo, useState } from 'react';
import { useEmergencyStore, getStatusColor } from '@/stores/emergency-store';
import type { Alert } from '@/stores/emergency-store';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  AlertTriangle,
  Flame,
  Heart,
  Shield,
  AlertCircle,
  MapPin,
  Clock,
  User,
  XCircle,
  CheckCircle,
  Loader2,
  Building2,
} from 'lucide-react';

function getAlertIcon(type: Alert['type']) {
  switch (type) {
    case 'SOS':
      return <AlertTriangle className="w-5 h-5" />;
    case 'FIRE':
      return <Flame className="w-5 h-5" />;
    case 'MEDICAL':
      return <Heart className="w-5 h-5" />;
    case 'INTRUSION':
      return <Shield className="w-5 h-5" />;
    case 'PANIC':
      return <AlertCircle className="w-5 h-5" />;
    default:
      return <AlertCircle className="w-5 h-5" />;
  }
}

function getAlertTypeName(type: Alert['type']) {
  switch (type) {
    case 'SOS':
      return 'SOS';
    case 'FIRE':
      return 'Incendio';
    case 'MEDICAL':
      return 'Médica';
    case 'INTRUSION':
      return 'Intrusión';
    case 'PANIC':
      return 'Pánico';
    default:
      return type;
  }
}

function getAlertTypeColor(type: Alert['type']) {
  switch (type) {
    case 'SOS':
      return 'bg-red-500';
    case 'FIRE':
      return 'bg-orange-500';
    case 'MEDICAL':
      return 'bg-pink-500';
    case 'INTRUSION':
      return 'bg-purple-500';
    case 'PANIC':
      return 'bg-red-600';
    default:
      return 'bg-gray-500';
  }
}

function getStatusName(status: Alert['status']) {
  switch (status) {
    case 'PENDING':
      return 'Pendiente';
    case 'ASSIGNED':
      return 'Asignada';
    case 'EN_ROUTE':
      return 'En camino';
    case 'ON_SCENE':
      return 'En sitio';
    case 'IN_PROGRESS':
      return 'En proceso';
    default:
      return status;
  }
}

/** Quick Falsa-alarma / Resolver buttons rendered inside a panel-ticket card.
 *  Stops propagation so clicking them doesn't also open the detail panel. */
function QuickCloseButtons({ alertId }: { alertId: string }) {
  const { updateAlertStatus } = useEmergencyStore();
  const [busy, setBusy] = useState<'CANCELLED' | 'RESOLVED' | null>(null);
  const [confirm, setConfirm] = useState<'CANCELLED' | 'RESOLVED' | null>(null);
  const [cancelNote, setCancelNote] = useState('');

  const handle = async (action: 'CANCELLED' | 'RESOLVED', e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm !== action) {
      setConfirm(action);
      setCancelNote('');
      return;
    }
    // CANCELLED requires an operator note.
    if (action === 'CANCELLED' && !cancelNote.trim()) return;
    setBusy(action);
    await updateAlertStatus(alertId, action,
      action === 'CANCELLED'
        ? cancelNote.trim()
        : 'Alarma verificada y resuelta por operador.',
    );
    setBusy(null);
    setConfirm(null);
    setCancelNote('');
  };

  return (
    <div className="flex flex-col gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
      {confirm === 'CANCELLED' && (
        <input
          type="text"
          autoFocus
          placeholder="Motivo de cancelación (requerido)"
          value={cancelNote}
          onChange={(e) => setCancelNote(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="w-full px-2 py-1 text-xs rounded border border-red-500/40 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-red-500"
        />
      )}
      <div className="flex gap-1.5">
      <button
        type="button"
        disabled={!!busy}
        onClick={(e) => handle('CANCELLED', e)}
        className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors disabled:opacity-50 ${
          confirm === 'CANCELLED'
            ? 'bg-red-500 text-white border-red-500'
            : 'bg-transparent text-red-400 border-red-500/40 hover:bg-red-500/10'
        }`}
      >
        {busy === 'CANCELLED' ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
        {confirm === 'CANCELLED' ? (cancelNote.trim() ? 'Confirmar' : 'Escribe el motivo') : 'Falsa alarma'}
      </button>
      <button
        type="button"
        disabled={!!busy}
        onClick={(e) => handle('RESOLVED', e)}
        className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors disabled:opacity-50 ${
          confirm === 'RESOLVED'
            ? 'bg-emerald-500 text-white border-emerald-500'
            : 'bg-transparent text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10'
        }`}
      >
        {busy === 'RESOLVED' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
        {confirm === 'RESOLVED' ? 'Confirmar' : 'Resolver'}
      </button>
      {confirm && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setConfirm(null); setCancelNote(''); }}
          className="px-1.5 py-1 text-[11px] text-gray-500 hover:text-gray-300"
        >×</button>
      )}
      </div>
    </div>
  );
}

export function AlertQueue() {
  const { activeAlerts, selectAlert, alertsLoading, bulkUpdateAlertStatus } = useEmergencyStore();
  const [filter, setFilter] = useState<'pending' | 'processed'>('pending');
  // Track which group is currently being resolved so we can disable the
  // button + show a spinner while the round-trip completes.
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);
  // Per-group confirm state (false alarm / resolved). The operator must
  // click twice to avoid wiping a flood of legit alerts by accident.
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<'CANCELLED' | 'RESOLVED' | null>(null);

  // "Pendientes" = aún no atendidas (status PENDING). "Procesadas" = ya
  // tomadas por un operador / en ruta / en sitio / en proceso. Las
  // resueltas/cerradas no llegan a esta lista (fetchActiveAlerts las filtra).
  const filteredAlerts = activeAlerts.filter((a) =>
    filter === 'pending' ? a.status === 'PENDING' : a.status !== 'PENDING'
  );

  // FIFO: oldest first. PENDING outranks any other status (newest pending
  // still shows above resolved-in-progress). Within the same status, oldest
  // first so the operator works through them in the order they arrived.
  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
    if (a.status !== 'PENDING' && b.status === 'PENDING') return 1;
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });

  // Group consecutive alerts by their effective "source". Mobile-client SOS
  // tickets group by client_id; panel-originated tickets (no client_id) group
  // by site_id (preferred) or account_id, so a single panel that floods the
  // queue with 30 panic events shows up as ONE collapsible batch instead of
  // 30 separate "Cliente" rows. The grouping label uses site_name / account
  // name when available so the operator can identify the source at a glance.
  type Group = {
    key: string;
    title: string;
    subtitle: string | null;
    items: Alert[];
  };
  const groups: Group[] = useMemo(() => {
    const out: Group[] = [];
    for (const alert of sortedAlerts) {
      const key =
        alert.client_id
          ? `client:${alert.client_id}`
          : alert.site_id
            ? `site:${alert.site_id}`
            : alert.account_id
              ? `account:${alert.account_id}`
              : `noid:${alert.client_name ?? alert.site_name ?? alert.account_name ?? 'cliente'}`;
      const title =
        alert.site_name ??
        alert.account_name ??
        alert.client_name ??
        'Cliente';
      const subtitle = alert.account_number
        ? `Cuenta ${alert.account_number}`
        : null;
      const last = out[out.length - 1];
      if (last && last.key === key) {
        last.items.push(alert);
      } else {
        out.push({ key, title, subtitle, items: [alert] });
      }
    }
    return out;
  }, [sortedAlerts]);

  const pendingCount = activeAlerts.filter((a) => a.status === 'PENDING').length;
  const processedCount = activeAlerts.length - pendingCount;

  if (alertsLoading && activeAlerts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400">
          <div className="w-5 h-5 border-2 border-gray-400 dark:border-slate-400 border-t-transparent rounded-full animate-spin" />
          <span>Cargando alertas...</span>
        </div>
      </div>
    );
  }

  const tabs = (
    <div className="flex border-b border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/50">
      <button
        onClick={() => setFilter('pending')}
        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
          filter === 'pending'
            ? 'border-red-500 text-red-500 dark:text-red-400'
            : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
        }`}
      >
        Pendientes ({pendingCount})
      </button>
      <button
        onClick={() => setFilter('processed')}
        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
          filter === 'processed'
            ? 'border-blue-500 text-blue-500 dark:text-blue-400'
            : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
        }`}
      >
        En Proceso ({processedCount})
      </button>
    </div>
  );

  if (activeAlerts.length === 0) {
    return (
      <div className="h-full flex flex-col">
        {tabs}
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin alertas activas</h3>
          <p className="text-gray-500 dark:text-slate-400 text-sm">
            Las nuevas alertas aparecerán aquí en tiempo real
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {tabs}

      {/* Header with pending count (only on pending tab) */}
      {filter === 'pending' && pendingCount > 0 && (
        <div className="p-3 bg-red-500/10 border-b border-red-500/30">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-4 h-4 animate-pulse" />
            <span className="font-medium">
              {pendingCount} alerta{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* Empty state for the active tab */}
      {groups.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="w-12 h-12 bg-gray-200 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
            <Shield className="w-6 h-6 text-gray-400 dark:text-slate-500" />
          </div>
          <p className="text-gray-500 dark:text-slate-400 text-sm">
            {filter === 'pending'
              ? 'No hay alertas pendientes'
              : 'No hay alertas en proceso actualmente'}
          </p>
        </div>
      )}

      {/* Alert list, grouped by client / site / account */}
      <div className="flex-1 overflow-y-auto">
        {groups.map((group) => {
          const isBatch = group.items.length > 1;
          const pendingItems = group.items.filter((a) => a.status === 'PENDING');
          const canBulk = isBatch && pendingItems.length > 1;
          const isThisGroupBusy = bulkBusy === group.key;
          const isConfirming = confirmKey === group.key;

          const handleBulk = async (action: 'CANCELLED' | 'RESOLVED') => {
            if (!isConfirming || confirmAction !== action) {
              setConfirmKey(group.key);
              setConfirmAction(action);
              return;
            }
            setBulkBusy(group.key);
            const ids = pendingItems.map((a) => a.id);
            const notes =
              action === 'CANCELLED'
                ? `Falsa alarma — procesado en lote (${ids.length} alertas) desde el centro de monitoreo.`
                : `Resuelto en lote (${ids.length} alertas) desde el centro de monitoreo.`;
            await bulkUpdateAlertStatus(ids, action, notes);
            setBulkBusy(null);
            setConfirmKey(null);
            setConfirmAction(null);
          };

          return (
            <div key={group.key} className={isBatch ? 'border-b-2 border-amber-500/40' : ''}>
              {isBatch && (
                <div className="px-3 py-2 bg-amber-500/10 border-b border-amber-500/20 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-amber-700 dark:text-amber-300 text-xs font-semibold truncate">
                        {group.items.length} alertas · {group.title}
                      </p>
                      {group.subtitle && (
                        <p className="text-amber-600/80 dark:text-amber-400/70 text-[10px] truncate">
                          {group.subtitle}
                        </p>
                      )}
                    </div>
                    {canBulk && pendingItems.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-500 dark:text-red-300 whitespace-nowrap">
                        {pendingItems.length} pend.
                      </span>
                    )}
                  </div>
                  {canBulk && (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleBulk('CANCELLED')}
                        disabled={isThisGroupBusy}
                        className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium border transition-colors disabled:opacity-50 ${
                          isConfirming && confirmAction === 'CANCELLED'
                            ? 'bg-red-500 text-white border-red-500 hover:bg-red-600'
                            : 'bg-white dark:bg-slate-800 text-red-600 dark:text-red-300 border-red-300 dark:border-red-700/40 hover:bg-red-50 dark:hover:bg-red-900/20'
                        }`}
                      >
                        {isThisGroupBusy ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        {isConfirming && confirmAction === 'CANCELLED'
                          ? `Confirmar (${pendingItems.length})`
                          : 'Falsa alarma · lote'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleBulk('RESOLVED')}
                        disabled={isThisGroupBusy}
                        className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium border transition-colors disabled:opacity-50 ${
                          isConfirming && confirmAction === 'RESOLVED'
                            ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
                            : 'bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700/40 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                        }`}
                      >
                        {isThisGroupBusy ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <CheckCircle className="w-3 h-3" />
                        )}
                        {isConfirming && confirmAction === 'RESOLVED'
                          ? `Confirmar (${pendingItems.length})`
                          : 'Resolver · lote'}
                      </button>
                      {isConfirming && (
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmKey(null);
                            setConfirmAction(null);
                          }}
                          className="px-2 py-1.5 rounded-md text-[11px] text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {group.items.map((alert) => {
                const statusColors = getStatusColor(alert.status);
                const isPending = alert.status === 'PENDING';
                // Panel-originated tickets have source_arc_event_id and no client_id.
                // For these we show site name as the primary identifier.
                const isPanelTicket = !!alert.source_arc_event_id && !alert.client_id;
                // Extract zone detail from description: "Alarma — Site (CID 1131 · Zona 3 · Sala)"
                // Show everything after the last "(" as supplementary info.
                const descDetail = alert.description
                  ? alert.description.replace(/^[^(]+\(/, '').replace(/\)$/, '')
                  : null;

                return (
                  <button
                    key={alert.id}
                    onClick={() => selectAlert(alert)}
                    className={`w-full p-4 border-b border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors text-left ${
                      isPending ? 'bg-red-500/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Type icon */}
                      <div
                        className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${getAlertTypeColor(
                          alert.type
                        )} ${isPending ? 'animate-pulse' : ''}`}
                      >
                        <span className="text-white">{getAlertIcon(alert.type)}</span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {getAlertTypeName(alert.type)}
                          </span>
                          <span className="text-gray-500 dark:text-slate-500 text-sm">#{alert.ticket_number}</span>
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${statusColors.bg} ${statusColors.text}`}
                          >
                            {getStatusName(alert.status)}
                          </span>
                        </div>

                        {/* Site name (panel) or client name (SOS) */}
                        <div className="flex items-center gap-1 text-gray-700 dark:text-slate-200 text-sm font-medium mb-0.5">
                          {isPanelTicket
                            ? <Building2 className="w-3 h-3 flex-shrink-0 text-amber-400" />
                            : <User className="w-3 h-3 flex-shrink-0" />
                          }
                          <span className="truncate">
                            {isPanelTicket
                              ? (
                                  [alert.account_name, alert.site_name]
                                    .filter(Boolean)
                                    .join(' · ')
                                  || 'Sitio desconocido'
                                )
                              : (alert.client_name ?? alert.account_name ?? 'Cliente')
                            }
                          </span>
                          {alert.account_number && (
                            <span className="text-[10px] text-gray-500 dark:text-slate-500 whitespace-nowrap">
                              · #{alert.account_number}
                            </span>
                          )}
                        </div>

                        {/* Zone / CID detail for panel tickets */}
                        {isPanelTicket && descDetail && (
                          <div className="text-[11px] text-amber-600 dark:text-amber-400/80 truncate mb-0.5">
                            {descDetail}
                          </div>
                        )}

                        {/* Address */}
                        {(alert.address || alert.site_address) && (
                          <div className="flex items-center gap-1 text-gray-500 dark:text-slate-500 text-xs mb-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{alert.address ?? alert.site_address}</span>
                          </div>
                        )}

                        {/* Time */}
                        <div className="flex items-center gap-1 text-gray-500 dark:text-slate-500 text-xs">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(new Date(alert.created_at), {
                              addSuffix: true,
                              locale: es,
                            })}
                          </span>
                          {alert.patrol_name && (
                            <>
                              <span className="mx-1">•</span>
                              <span className="text-green-400">{alert.patrol_name}</span>
                            </>
                          )}
                        </div>

                        {/* Quick close buttons for panel tickets (no chat needed) */}
                        {isPanelTicket && isPending && (
                          <QuickCloseButtons alertId={alert.id} />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
